import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useState } from "react";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { EmailSentModal } from "@components/modals/EmailSentModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import CheckBox from "@components/ui/selection/CheckBox";
import SearchField from "@components/ui/textfields/SearchField";
import { colors } from "sexy";
import { EmailSent } from "types";
import { Timestamp } from "firebase/firestore";

// ─── Date helper ──────────────────────────────────────────────────────────────

function formatEmailDate(ts: Timestamp): string {
  const date = new Date(ts.seconds * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 1 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (diffDays < 7) {
    return date.toLocaleDateString("fr-FR", { weekday: "short" });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  }
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// ─── Email row ─────────────────────────────────────────────────────────────────

function EmailRow({
  email,
  projectName,
  isSelected,
  anySelected,
  onToggle,
  onClick,
}: {
  email: EmailSent;
  projectName: string;
  isSelected: boolean;
  anySelected: boolean;
  onToggle: () => void;
  onClick: () => void;
}) {
  const attachmentCount =
    (email.files?.length || 0) + (email.photos_id?.length || 0);
  const recipients = email.emails_to?.join(", ") || "";
  const bodyPreview = email.body?.replace(/\n/g, " ").trim() || "";

  return (
    <div
      onClick={onClick}
      className={`group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer transition-colors ${
        isSelected ? "bg-blue-50" : "bg-white hover:bg-neutral-50"
      }`}
    >
      {/* Checkbox — always visible when any selected, otherwise appears on hover */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex-shrink-0 transition-opacity ${
          anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        <CheckBox checked={isSelected} onChange={() => onToggle()} />
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 gap-0.5">
        {/* Subject + preview on one line */}
        <div className="flex flex-row items-baseline gap-2 min-w-0">
          <span className="text-sm font-semibold text-neutral-900 truncate shrink-0 max-w-xs">
            {email.subject || "(sans sujet)"}
          </span>
          {bodyPreview && (
            <span className="text-sm text-neutral-400 truncate">
              {bodyPreview}
            </span>
          )}
        </div>
        {/* Recipients */}
        <div className="text-xs text-neutral-400 truncate">
          À : {recipients || "—"}
        </div>
      </div>

      {/* Right side: project badge + attachment + date */}
      <div className="flex flex-row items-center gap-3 flex-shrink-0">
        {projectName && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 max-w-32 truncate">
            {projectName}
          </span>
        )}
        {attachmentCount > 0 && (
          <div className="flex flex-row items-center gap-1 text-neutral-400">
            <Icon1 name="join" size={14} color={colors.neutral_400} />
            <span className="text-xs">{attachmentCount}</span>
          </div>
        )}
        <span className="text-xs text-neutral-400 w-12 text-right">
          {email.created_at ? formatEmailDate(email.created_at) : "—"}
        </span>
      </div>
    </div>
  );
}

// ─── Screen ────────────────────────────────────────────────────────────────────

export default function EmailsSentScreen() {
  const sentEmails = useProjectStore((state) => state.sentEmails);
  const fetchSentEmails = useProjectStore((state) => state.fetchSentEmails);
  const projects = useProjectStore((state) => state.projects);
  const fetchProject = useProjectStore((state) => state.fetchProject);
  const removeSentEmails = useProjectStore((state) => state.removeSentEmails);
  const orgData = useDataStore((state) => state.orgData);

  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<import("types").Project | undefined>();

  useEffect(() => {
    if (selectedEmailId) {
      const projectId = sentEmails.find((e) => e.id === selectedEmailId)?.project_id;
      if (projectId) fetchProject(projectId).then(setSelectedProject);
      else setSelectedProject(undefined);
    } else {
      setSelectedProject(undefined);
    }
  }, [selectedEmailId]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (orgData?.id) {
      fetchSentEmails(orgData.id);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const sorted = [...sentEmails].sort(
      (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
    );
    if (!q) return sorted;
    return sorted.filter((e) => {
      const projectName =
        projects
          .find((p) => p.id === e.project_id)
          ?.infos.general.name?.toLowerCase() || "";
      return (
        e.subject?.toLowerCase().includes(q) ||
        e.body?.toLowerCase().includes(q) ||
        e.emails_to?.some((addr) => addr.toLowerCase().includes(q)) ||
        projectName.includes(q)
      );
    });
  }, [sentEmails, projects, search]);


  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((e) => e.id));
    }
  };

  const handleDeleteSelected = () => {
    removeSentEmails(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} email(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  const allSelected =
    filtered.length > 0 && selectedIds.length === filtered.length;

  return (
    <div className="flex flex-col  overflow-auto max-h-screen">
      {selectedEmailId && (
        <EmailSentModal
          onClose={() => setSelectedEmailId(null)}
          email_sent_id={selectedEmailId}
          sentEmails={sentEmails}
          activeProject={selectedProject}
          orgData={orgData || undefined}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} email(s) ?`}
          message="Cette action est irréversible. Tous les emails sélectionnés seront supprimés définitivement."
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}

      <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
        <div className="flex flex-col w-full max-w-1k gap-4">
          {/* Header */}
          <div className="flex flex-row w-full justify-between items-start">
            <div className="flex flex-col w-full max-w-400">
              <Text1>Emails envoyés</Text1>
              <Text2>Retrouvez tous vos emails envoyés ici.</Text2>
            </div>
            <div className="flex flex-row gap-2 items-start">
              {selectedIds.length > 0 && (
                <SecondaryButton
                  danger={true}
                  startIcon={
                    <Icon1 name="delete" size={22} color={colors.red_500} />
                  }
                  label={`Supprimer la sélection (${selectedIds.length})`}
                  variant="large"
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
            </div>
          </div>

          {/* Search */}
          <SearchField
            placeholder="Rechercher par sujet, destinataire ou projet…"
            value={search}
            onChange={(v) => setSearch(v)}
          />

          {/* List */}
          <div className="flex flex-col rounded-lg border border-neutral-200 overflow-hidden bg-white">
            {/* List header */}
            <div className="flex flex-row items-center gap-3 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
              <div className="cursor-pointer flex-shrink-0">
                <CheckBox
                  checked={allSelected}
                  onChange={() => handleSelectAll()}
                />
              </div>
              <span className="text-xs text-neutral-500 flex-1">
                {filtered.length} email{filtered.length !== 1 ? "s" : ""}
                {search ? " trouvé" + (filtered.length !== 1 ? "s" : "") : ""}
              </span>
              {selectedIds.length > 0 && (
                <span className="text-xs text-neutral-500">
                  {selectedIds.length} sélectionné
                  {selectedIds.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Rows */}
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Icon1 name="email" size={32} color={colors.neutral_300} />
                <span className="text-sm text-neutral-400">
                  {search
                    ? "Aucun email ne correspond à la recherche."
                    : "Aucun email envoyé."}
                </span>
              </div>
            ) : (
              filtered.map((email) => (
                <EmailRow
                  key={email.id}
                  email={email}
                  projectName={
                    projects.find((p) => p.id === email.project_id)?.infos
                      .general.name || ""
                  }
                  isSelected={selectedIds.includes(email.id)}
                  anySelected={selectedIds.length > 0}
                  onToggle={() => handleToggle(email.id)}
                  onClick={() => setSelectedEmailId(email.id)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
